import { FileCheck2, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
} from '@/components/ui';
import { useConsentTemplateAccess } from '@/features/consent-templates/access';
import { useConsentTemplates } from '@/features/consent-templates/api';
import { groupByKey } from '@/features/consent-templates/group';
import {
  CONSENT_PROCEDURES,
  CONSENT_TEMPLATE_STATUSES,
  type ConsentTemplateFilters,
  type ConsentTemplateGroup,
  type ConsentTemplateStatus,
} from '@/features/consent-templates/types';
import { PageContainer } from '@/layouts/PageContainer';
import { ConsentTemplateDrawer } from './ConsentTemplateDrawer';
import { ConsentTemplateForm } from './ConsentTemplateForm';
import { procedureName, STATUS_LABEL, STATUS_TONE } from './consent-format';
import styles from './ConsentTemplates.module.css';

const STATUS_OPTIONS = CONSENT_TEMPLATE_STATUSES.map((s) => ({
  value: s,
  label: STATUS_LABEL[s],
}));
const PROCEDURE_OPTIONS = CONSENT_PROCEDURES.map((p) => ({
  value: p.code,
  label: p.name,
}));

/**
 * "Consent templates" (RL-1136, API RL-908): the procedure consent wording
 * patients read and sign in the app, one row per template key with its live
 * (approved) revision and any draft. Doctors and clinic managers write drafts
 * in English and Bahasa Melayu; a doctor approves them; either retires them.
 */
export function ConsentTemplatesPage() {
  const access = useConsentTemplateAccess();
  const [filters, setFilters] = useState<ConsentTemplateFilters>({
    status: '',
  });
  const [procedure, setProcedure] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const q = useConsentTemplates(filters);

  const groups = useMemo(
    () =>
      groupByKey(q.data ?? [])
        .filter((g) => !procedure || g.latest.procedureCode === procedure)
        .sort(
          (a, b) =>
            procedureName(a.latest.procedureCode).localeCompare(
              procedureName(b.latest.procedureCode),
            ) || a.key.localeCompare(b.key),
        ),
    [q.data, procedure],
  );
  const filtered = !!filters.status || !!procedure;

  return (
    <PageContainer>
      <Card
        title="Consent templates"
        flush
        actions={
          <div className={styles.filters}>
            <Field label="Status">
              <Select
                value={filters.status}
                placeholder="All statuses"
                options={STATUS_OPTIONS}
                onChange={(e) =>
                  setFilters({
                    status: e.target.value as ConsentTemplateStatus | '',
                  })
                }
              />
            </Field>
            <Field label="Procedure">
              <Select
                value={procedure}
                placeholder="All procedures"
                options={PROCEDURE_OPTIONS}
                onChange={(e) => setProcedure(e.target.value)}
              />
            </Field>
            {access.canManage && (
              <Button
                variant="primary"
                icon={<Plus size={16} aria-hidden="true" />}
                onClick={() => setCreating(true)}
              >
                New template
              </Button>
            )}
          </div>
        }
      >
        <p className={styles.intro}>
          The wording patients read and sign in the app before a procedure, in
          English and Bahasa Melayu. Only an approved revision is offered to
          patients; approving a new revision retires the previous one, and forms
          already signed keep the wording they signed.
          {!access.canApprove && ' A doctor approves each draft.'}
        </p>
        <DataTable
          caption="Consent templates by procedure"
          rows={groups}
          rowKey={(g) => g.key}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={
            <EmptyState
              icon={<FileCheck2 size={22} />}
              title={
                filtered
                  ? 'No templates match these filters'
                  : 'No consent templates yet'
              }
              body={
                filtered
                  ? undefined
                  : access.canManage
                    ? 'Write the consent wording for a procedure with “New template”. A doctor approves it before patients can sign it.'
                    : 'Templates appear here once the clinic writes them.'
              }
            />
          }
          columns={[
            {
              key: 'procedure',
              header: 'Procedure',
              render: (g) => procedureName(g.latest.procedureCode),
            },
            {
              key: 'template',
              header: 'Template',
              render: (g) => (
                <>
                  <p className={styles.name}>{g.latest.title.en}</p>
                  <p className={styles.key}>{g.key}</p>
                </>
              ),
            },
            {
              key: 'service',
              header: 'Service',
              render: (g) =>
                (g.approved ?? g.latest).service?.name ?? (
                  <span className={styles.muted}>Chosen per booking</span>
                ),
            },
            {
              key: 'live',
              header: 'Patients sign',
              render: (g) => <LiveCell g={g} partial={!!filters.status} />,
            },
            {
              key: 'draft',
              header: 'Draft',
              render: (g) =>
                g.draft ? (
                  <Badge tone={STATUS_TONE.draft} dot>
                    Revision {g.draft.revision}
                  </Badge>
                ) : (
                  <span className={styles.muted}>—</span>
                ),
            },
            {
              key: 'open',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right',
              render: (g) => (
                <Button
                  size="sm"
                  onClick={() => setOpenKey(g.key)}
                  aria-label={`Open the consent template ${g.latest.title.en}`}
                >
                  Open
                </Button>
              ),
            },
          ]}
        />
      </Card>
      {openKey && (
        <ConsentTemplateDrawer
          templateKey={openKey}
          onClose={() => setOpenKey(null)}
        />
      )}
      {creating && (
        <ConsentTemplateForm
          mode={{ kind: 'create' }}
          onClose={() => setCreating(false)}
          onSaved={(t) => {
            setCreating(false);
            setOpenKey(t.key);
          }}
        />
      )}
    </PageContainer>
  );
}

function LiveCell({
  g,
  partial,
}: {
  g: ConsentTemplateGroup;
  partial: boolean;
}) {
  if (g.approved)
    return (
      <Badge tone={STATUS_TONE.approved} dot>
        Revision {g.approved.revision} approved
      </Badge>
    );
  // A status filter hides the other revisions: say only what is listed.
  if (partial)
    return (
      <Badge tone={STATUS_TONE[g.latest.status]} dot>
        Revision {g.latest.revision}{' '}
        {STATUS_LABEL[g.latest.status].toLowerCase()}
      </Badge>
    );
  return (
    <Badge tone="danger" dot>
      None approved
    </Badge>
  );
}
